import React, { useState, useCallback } from "react";
import { View, StyleSheet, Platform, Image, ViewStyle, ImageStyle } from "react-native";
import type * as ExpoImagePicker from "expo-image-picker";
import CustomText from "../CustomText/CustomText";
import CustomButton from "../CustomButton/CustomButton";
import { NotePencil } from "@phosphor-icons/react/dist/ssr";
import { useAppConfig } from "../../../../AppConfigProvider";
import { StoragePathType } from "../../../../graphql/generated/graphql";
import useFileUpload, { generatePublicUrl } from "../../../../hooks/useFileUpload";

interface ImageUploadProps {
  onChange: (value: string) => void;
  value?: string;
  storagePath: StoragePathType;
  containerStyle?: ViewStyle;
  imageStyle?: ImageStyle;
  buttonStyle?: ViewStyle;
  placeholderContent?: React.ReactNode;
  aspectRatio?: [number, number];
  allowsEditing?: boolean;
  buttonText?: string;
  changeButtonText?: string;
  dropzoneText?: string;
  dropzoneSubtext?: string;
  onError?: (error: string) => void;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  onChange,
  value,
  storagePath,
  containerStyle,
  imageStyle,
  buttonStyle,
  placeholderContent,
  aspectRatio = [16, 9],
  allowsEditing = true,
  buttonText = "Select Image",
  changeButtonText = "Change Image",
  dropzoneText = "Drag & drop or click to browse",
  dropzoneSubtext = "JPG or PNG format, Max file size: 2mb",
  onError,
}) => {
  const { currentDomain } = useAppConfig();
  const [isDragging, setIsDragging] = useState(false);

  const { handleServerUpload, handleFileUpload, allowedMimeTypes } = useFileUpload({
    storagePath: storagePath,
    onError: onError,
    isPublic: true,
  });

  const handleImageSelection = useCallback(async () => {
    const result = await handleFileUpload();
    if (result.length > 0) {
      onChange(generatePublicUrl(currentDomain, result[0].file.filePath));
    }
  }, [onChange, allowsEditing, aspectRatio, onError]);

  const handleFileChange = useCallback(
    async (uploadingFile: File) => {
      let uri = null;

      const reader = new FileReader();
      reader.onloadend = async () => {
        const result = reader.result as string;
        uri = result;

        const { file, signedUrl } = await handleServerUpload({
          uri,
          mimeType: uploadingFile.type,
          size: uploadingFile.size,
          name: uploadingFile.name,
        });

        const publicUrl = generatePublicUrl(currentDomain, file.filePath);

        onChange(publicUrl);
      };
      reader.readAsDataURL(uploadingFile);
    },
    [onChange, onError, handleServerUpload]
  );

  if (Platform.OS !== "web") {
    return (
      <View style={[styles.mobileUploadContainer, containerStyle]}>
        {value ? <Image source={{ uri: value }} style={[styles.mobilePreview, imageStyle]} /> : placeholderContent}
        <CustomButton
          text={value ? changeButtonText : buttonText}
          onPress={handleImageSelection}
          styleType="secondary"
          size="m"
          style={[styles.uploadButton, buttonStyle]}
          textStyle={styles.uploadButtonText}
          leftIcon={null}
          rightIcon={null}
          disabled={false}
          textProps={{}}
        />
      </View>
    );
  }

  const dropZoneStyle = {
    position: "relative",
    width: "100%",
    height: 120,
    borderWidth: 2,
    borderStyle: "dashed",
    borderColor: isDragging ? "#3E68FE" : "#D1D5DB",
    borderRadius: 6,
    backgroundColor: isDragging ? "#EEF2FF" : "#F9FAFB",
    overflow: "visible",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...containerStyle,
  };

  const imageContainerStyle = {
    width: "100%",
    height: "100%",
    overflow: "visible",
    ...imageStyle,
  };

  const imagePreviewStyle = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  };

  const editOverlayStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "absolute",
    bottom: 8,
    right: 8,
    backgroundColor: "#FFFFFF",
    color: "#3E68FE",
    width: 40,
    height: 40,
    borderRadius: 20,
    boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.1)",
    borderWidth: 1,
    borderColor: "#E5E7EB",
  };

  const handleClick = (e) => {
    if (e.target === e.currentTarget || (e.target as HTMLElement).closest('[data-dropzone-content="true"]')) {
      document.getElementById("file-input")?.click();
    }
  };

  return (
    <>
      <div
        style={dropZoneStyle as React.CSSProperties}
        onClick={handleClick}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(false);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(true);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(false);

          const file = e.dataTransfer?.files?.[0];
          if (file) {
            handleFileChange(file);
          }
        }}>
        {value ? (
          <div style={imageContainerStyle as React.CSSProperties}>
            <img src={value} style={imagePreviewStyle as React.CSSProperties} alt="Image preview" />
            <div style={editOverlayStyle as React.CSSProperties}>
              <NotePencil width={24} height={24} />
            </div>
          </div>
        ) : (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
            data-dropzone-content="true">
            {(placeholderContent && (
              <div style={imageContainerStyle as React.CSSProperties}>
                {placeholderContent}
                <div style={editOverlayStyle as React.CSSProperties}>
                  <NotePencil width={24} height={24} />
                </div>
              </div>
            )) || (
              <div style={imageContainerStyle as React.CSSProperties}>
                <CustomText text={dropzoneText} useTranslationText={false} size="xs" style={styles.dropZoneText} />
                <CustomText
                  text={dropzoneSubtext}
                  useTranslationText={false}
                  size="xs"
                  style={styles.dropZoneSubtext}
                />
                <div style={editOverlayStyle as React.CSSProperties}>
                  <NotePencil width={24} height={24} />
                </div>
              </div>
            )}
          </div>
        )}
        <input
          id="file-input"
          type="file"
          accept={allowedMimeTypes.join(",")}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              handleFileChange(file);
            }
          }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0,
            width: "100%",
            height: "100%",
            cursor: "pointer",
          }}
        />
      </div>
    </>
  );
};

const styles = StyleSheet.create({
  mobileUploadContainer: {
    width: "100%",
    alignItems: "center",
    gap: 12,
  },
  mobilePreview: {
    // width: "100%",
    height: 120,
    borderRadius: 6,
    backgroundColor: "#F9FAFB",
  },
  uploadButton: {
    width: "100%",
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  uploadButtonText: {
    color: "#111827",
  },
  dropZoneText: {
    color: "#111827",
    marginBottom: 4,
  },
  dropZoneSubtext: {
    color: "#6B7280",
  },
});

export default ImageUpload;
